import { useEffect, useMemo } from 'react';
import {
  TravellerWithCollectionMembershipsDTO,
  useGetTravellersPerOrganization,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { Autocomplete } from '../Autocomplete';
import { collator } from '@/utils/collator';
import { IconArrowRight, IconSearch, IconUser } from '@/icons';
import Button from '../core/Button';
import { useTenantID } from '@/hooks/useTenantID';
import { getTravellerStatus } from '@/helpers/status';
import { isStudentCollectionType } from '@/helpers/collectionTypes';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';
import { useShowOrgLevelNav } from '@/hooks/useShowOrgLevelNav';
import { BearerSelectorForTicket } from './BearerSelectorForTicket';
import {
  TemporaryTravellerForm,
  TemporaryTravellerData,
} from './TemporaryTravellerForm';
import { Section } from '../Section';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';

interface Props {
  initialTravellerId: string | undefined;
  selectedTraveller: TravellerWithCollectionMembershipsDTO | undefined;
  onTravellerSelect: (traveller: TravellerWithCollectionMembershipsDTO) => void;
  selectedBearer: string;
  onBearerSelect: (bearer: string) => void;
  isTemporaryTraveller: boolean;
  onTemporaryTravellerModeChange: (isTemporary: boolean) => void;
  onTemporaryTravellerDataChange: (
    data: TemporaryTravellerData | undefined,
  ) => void;
  onBobcatEmailChange?: (email: string) => void;
  onSaveBobcatEmailChange?: (saveEmail: boolean) => void;
}

export const TravellerSelection = ({
  initialTravellerId,
  selectedTraveller,
  onTravellerSelect,
  selectedBearer,
  onBearerSelect,
  isTemporaryTraveller,
  onTemporaryTravellerModeChange,
  onTemporaryTravellerDataChange,
  onBobcatEmailChange,
  onSaveBobcatEmailChange,
}: Props) => {
  const { formatMessage } = useIntl();
  const tenantId = useTenantID();
  const [ref] = useAutoAnimateConditional<HTMLDivElement>();

  const { data: travellers } = useGetTravellersPerOrganization();

  const studentsDisabled = useStudentsDisabled();
  const accessibleStudentCategories = useAccessibleStudentCategories();
  const hasStudentAccess =
    !studentsDisabled && accessibleStudentCategories.length > 0;
  const hasRegularAccess = useShowOrgLevelNav();
  const showTravellerTypeBadges = hasStudentAccess && hasRegularAccess;

  const activeTravellers = useMemo(() => {
    if (!travellers) {
      return [];
    }

    return travellers.filter(
      (travellerData) =>
        getTravellerStatus(travellerData) !== 'inactive' &&
        travellerData.memberships.some(
          (m) => m.active && hasDepartmentWriteAccess(m.department_id),
        ),
    );
  }, [travellers]);

  const options = useMemo(() => {
    if (!activeTravellers) {
      return [];
    }

    const opts = activeTravellers.map(
      ({ traveller: { id, first_name, last_name, email }, memberships }) => {
        const label =
          first_name || last_name
            ? `${first_name || ''} ${last_name || ''} ${email ? `(${email})` : ''}`
            : `${email}`;

        const activeMemberships = memberships.filter((m) => m.active);
        const isStudent = activeMemberships.some((m) =>
          isStudentCollectionType(m.collection_type),
        );
        const isRegular = activeMemberships.some(
          (m) => !isStudentCollectionType(m.collection_type),
        );

        return {
          label,
          value: id,
          endContent: showTravellerTypeBadges && (isStudent || isRegular) && (
            <span className="flex shrink-0 gap-1">
              {isStudent && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                  <FormattedMessage defaultMessage="Elev" />
                </span>
              )}
              {isRegular && (
                <span className="rounded-full bg-background-200 px-2 py-0.5 text-xs text-text-800">
                  <FormattedMessage defaultMessage="Resenär" />
                </span>
              )}
            </span>
          ),
        };
      },
    );

    return opts.sort((a, b) => {
      return collator.compare(a.label, b.label);
    });
  }, [activeTravellers, showTravellerTypeBadges]);

  useEffect(() => {
    if (initialTravellerId && activeTravellers) {
      const index = activeTravellers.findIndex(
        (t) => t.traveller.id === initialTravellerId,
      );
      if (index !== -1) {
        onTravellerSelect(activeTravellers[index]);
      }
    }
  }, [initialTravellerId, activeTravellers, onTravellerSelect]);

  return (
    <Section ref={ref}>
      <div className="flex w-full items-center justify-between gap-2">
        <h2 className="mb-6">
          <FormattedMessage defaultMessage="Välj resenär" />
        </h2>
        {selectedTraveller && !isTemporaryTraveller ? (
          <Button
            to="/$tenantId/travellers/$travellerId"
            params={{
              tenantId: tenantId,
              travellerId: selectedTraveller.traveller.id,
            }}
            startIcon={<IconUser />}
            endIcon={<IconArrowRight aria-hidden="true" />}
          >
            <FormattedMessage defaultMessage="Resenär" />
          </Button>
        ) : null}
      </div>
      <div className="mb-8 flex flex-wrap gap-1">
        <Button
          variant={!isTemporaryTraveller ? 'filled' : 'outline'}
          onClick={() => onTemporaryTravellerModeChange(false)}
          className="min-w-max flex-1"
        >
          <FormattedMessage defaultMessage="Befintlig resenär" />
        </Button>
        <Button
          variant={isTemporaryTraveller ? 'filled' : 'outline'}
          onClick={() => onTemporaryTravellerModeChange(true)}
          className="min-w-max flex-1"
        >
          <FormattedMessage defaultMessage="Tillfällig resenär" />
        </Button>
      </div>

      {!isTemporaryTraveller ? (
        <>
          <Autocomplete
            options={options}
            value={selectedTraveller?.traveller.id || ''}
            onValueChange={(value) => {
              const traveller = activeTravellers?.find(
                (t) => t.traveller.id === value,
              );
              if (traveller) {
                if (
                  traveller.traveller.id !== selectedTraveller?.traveller.id
                ) {
                  onBearerSelect('');
                  onBobcatEmailChange?.('');
                  onSaveBobcatEmailChange?.(true);
                }
                onTravellerSelect(traveller);
              }
            }}
            placeholder={formatMessage({ defaultMessage: 'Sök resenär' })}
            id="traveller-search"
            autoFocus={!initialTravellerId}
            startAdornment={<IconSearch />}
            className="mb-4"
          />
          {selectedTraveller && (
            <BearerSelectorForTicket
              traveller={selectedTraveller}
              onBearerSelect={onBearerSelect}
              selectedBearer={selectedBearer}
              onEmailChange={onBobcatEmailChange}
              onSaveEmailChange={onSaveBobcatEmailChange}
            />
          )}
        </>
      ) : (
        <TemporaryTravellerForm
          onTravellerDataChange={onTemporaryTravellerDataChange}
        />
      )}
    </Section>
  );
};
